/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calculator, Plus, Trash, Receipt, FileText, FloppyDisk, CaretRight, CaretDown, Lock,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Conveyancing Fees panel — sticky right-column card on the case file.
 *
 * Categories (top to bottom):
 *   1. Property        — price / chattels / allowances / deposit paid / SDLT
 *   2. Legal fees      — solicitor's fee + supplements (20% VAT)
 *   3. Disbursements   — search pack, HMLR, etc. (20% VAT)
 *   4. Third-party     — any external cost (no VAT)
 *   5. Money in        — money on account / mortgage advance / other receipts
 *
 * Every row has:
 *   • editable label
 *   • editable net amount
 *   • for VAT-bearing categories — VAT shown read-only (always 20%)
 *   • two checkboxes:  add → completion statement,  add → client invoice
 *
 * SDLT row gets an extra "Calculate" link that opens the standard quote-style
 * SDLT modal (first-time buyer / second home / ltd company) and fills the
 * Property → SDLT row with the result.
 *
 * Two action buttons at the bottom generate downloadable PDFs from the rows
 * the user has ticked for each statement.
 */
const VAT_RATE = 0.20;
const fmt = (v) => `£${(Number(v) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

const CATEGORIES = [
  { key: "property",     title: "Property",          vatable: false, allowAdd: true },
  { key: "legal_fees",   title: "Legal fees",        vatable: true,  allowAdd: true },
  { key: "disbursements",title: "Disbursements",     vatable: false, allowAdd: true },
  { key: "third_party",  title: "Third-party costs", vatable: false, allowAdd: true },
  { key: "money_in",     title: "Money in",          vatable: false, allowAdd: true, isIncoming: true },
];

// Auto-generated disbursement lines that are ALSO billed to the firm. Deleting
// one must warn the solicitor (it drops off both the completion statement and
// the Usage & Billing report). Labels mirror routes/case_fees.py.
const BILLABLE_AUTO_LABELS = ["AML / ID Verification", "Case management software fee"];

export default function ConveyancingFeesCard({ caseId, isSolicitor }) {
  const { user } = useAuth();
  const canOverrideLockedFees = !!user?.can_override_locked_fees;
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [sdltOpen, setSdltOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/fees`);
      setDoc(data);
      setDirty(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load fees");
    } finally {
      setLoading(false);
    }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  // Mutators — all set `dirty=true` so the Save button lights up.
  const updateRow = useCallback((cat, rowId, patch) => {
    setDoc((d) => {
      if (!d) return d;
      const next = { ...d, [cat]: d[cat].map((r) => r.id === rowId ? { ...r, ...patch } : r) };
      // Recompute VAT + total locally for instant feedback (server is the
      // source of truth on Save).
      const isVatable = CATEGORIES.find((c) => c.key === cat)?.vatable;
      next[cat] = next[cat].map((r) => {
        if (r.id !== rowId) return r;
        const net = Number(r.amount) || 0;
        const vat = isVatable ? round2(net * VAT_RATE) : 0;
        return { ...r, vat, total: round2(net + vat) };
      });
      return next;
    });
    setDirty(true);
  }, []);
  const addRow = useCallback((cat) => {
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setDoc((d) => ({
      ...d,
      [cat]: [...(d[cat] || []), { id: tempId, label: "", amount: 0, vat: 0, total: 0, add_to_completion: false, add_to_invoice: false }],
    }));
    setDirty(true);
  }, []);
  const doRemove = useCallback((cat, rowId) => {
    setDoc((d) => ({ ...d, [cat]: d[cat].filter((r) => r.id !== rowId) }));
    setDirty(true);
  }, []);
  const removeRow = useCallback((cat, rowId) => {
    // Warn before deleting an auto-billed disbursement (ID check / case mgmt):
    // it also comes off the firm's Usage & Billing report once saved.
    const row = (doc?.[cat] || []).find((r) => r.id === rowId);
    const label = (row?.label || "").trim();
    if (BILLABLE_AUTO_LABELS.includes(label)) {
      setPendingDelete({ cat, rowId, label });
      return;
    }
    doRemove(cat, rowId);
  }, [doc, doRemove]);

  const save = useCallback(async (override) => {
    // Only trust an override that is actually a fees doc (confirmBillableDelete
    // passes one); ignore stray args like a click event from onClick={save}.
    const src = (override && override.property !== undefined) ? override : doc;
    if (!src) return null;
    setSaving(true);
    try {
      const payload = {
        property: src.property,
        legal_fees: src.legal_fees,
        disbursements: src.disbursements,
        third_party: src.third_party,
        money_in: src.money_in,
        sdlt_params: src.sdlt_params,
      };
      const { data } = await API.put(`/cases/${caseId}/fees`, payload);
      setDoc(data);
      setDirty(false);
      toast.success("Conveyancing fees saved");
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  }, [caseId, doc]);

  // Delete a billable auto line (ID check / case mgmt) AND persist immediately,
  // so it drops off the completion statement + Usage & Billing without needing a
  // separate Save click. Called from the confirmation dialog.
  const confirmBillableDelete = useCallback(async (cat, rowId) => {
    if (!doc) return;
    const nextDoc = { ...doc, [cat]: (doc[cat] || []).filter((r) => r.id !== rowId) };
    setDoc(nextDoc);
    await save(nextDoc);
  }, [doc, save]);

  const downloadPdf = useCallback(async (kind) => {
    // If the user ticked boxes / edited rows but hasn't clicked Save yet,
    // persist first so the PDF picks up the latest state. The PDF endpoint
    // reads from Mongo, not from the browser session — without this the
    // completion-statement PDF was missing rows the user had just ticked.
    if (dirty) {
      const saved = await save();
      if (!saved) return;
    }
    try {
      // Defensive cache-bust: some upstream proxies cache `.pdf` URLs by
      // extension even when the response sets `Cache-Control: no-store`.
      // Appending a fresh timestamp guarantees a unique URL per click.
      const ts = Date.now();
      const path = kind === "completion"
        ? `/cases/${caseId}/fees/completion-statement.pdf?t=${ts}`
        : `/cases/${caseId}/fees/invoice.pdf?t=${ts}`;
      const resp = await API.get(path, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = kind === "completion" ? "completion-statement.pdf" : "client-invoice.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  }, [caseId, dirty, save]);

  // Diagnostic action — calls the backend debug endpoint and copies the
  // JSON response onto the clipboard so the user can paste it back to us
  // when investigating Production rendering mismatches.
  const runDiag = useCallback(async () => {
    try {
      const { data } = await API.get(`/cases/${caseId}/fees/debug?t=${Date.now()}`);
      const payload = JSON.stringify(data, null, 2);
      try {
        await navigator.clipboard.writeText(payload);
        toast.success(`Diagnostics copied. Version: ${data.code_version}`);
      } catch {
        // Older browsers — fallback to opening in a new tab.
        const blob = new Blob([payload], { type: "application/json" });
        window.open(URL.createObjectURL(blob), "_blank", "noopener");
        toast.success(`Diagnostics opened in new tab. Version: ${data.code_version}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Diagnostics failed");
    }
  }, [caseId]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="conveyancing-fees-card-loading">
        <p className="text-xs text-slate-400">Loading fees…</p>
      </div>
    );
  }
  if (!doc) return null;
  const totals = doc.totals || {};

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="conveyancing-fees-card">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="text-sm font-semibold text-slate-900 flex items-center gap-2 hover:text-slate-700"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="conveyancing-fees-toggle"
        >
          {collapsed ? <CaretRight weight="bold" className="w-4 h-4" /> : <CaretDown weight="bold" className="w-4 h-4" />}
          <Receipt weight="bold" className="w-4 h-4 text-slate-500" /> Conveyancing fees
        </button>
        {isSolicitor && dirty && !collapsed && (
          <Button
            onClick={() => save()}
            disabled={saving}
            size="sm"
            className="h-7 px-2 text-xs gap-1 text-white"
            style={{ background: "#100f5d" }}
            data-testid="conveyancing-fees-save-btn"
          >
            <FloppyDisk weight="bold" className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </div>

      {!collapsed && (
        <>
          {CATEGORIES.map((cat) => (
            <FeeSection
              key={cat.key}
              category={cat}
              rows={doc[cat.key] || []}
              totals={totals[cat.key]}
              isSolicitor={isSolicitor}
              canOverrideLockedFees={canOverrideLockedFees}
              onChange={(rowId, patch) => updateRow(cat.key, rowId, patch)}
              onAdd={() => addRow(cat.key)}
              onRemove={(rowId) => removeRow(cat.key, rowId)}
              onCalcSdlt={cat.key === "property" ? () => setSdltOpen(true) : undefined}
            />
          ))}

          {/* Totals strip */}
          <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5 text-xs" data-testid="conveyancing-fees-totals">
            <Total label="Money out (incl. VAT)" value={totals.money_out_total} />
            <Total label="Money in" value={totals.money_in?.total} />
            <Total
              label={totals.balance_due >= 0 ? "Balance due from client" : "Balance due to client"}
              value={Math.abs(totals.balance_due || 0)}
              emphasise
            />
          </div>

          {/* PDF actions */}
          {isSolicitor && (
            <div className="mt-4 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => downloadPdf("completion")}
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  data-testid="fees-download-completion-btn"
                >
                  <FileText weight="bold" className="w-3.5 h-3.5" /> Completion stmt
                </Button>
                <Button
                  onClick={() => downloadPdf("invoice")}
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  data-testid="fees-download-invoice-btn"
                >
                  <FileText weight="bold" className="w-3.5 h-3.5" /> Client invoice
                </Button>
              </div>
              <button
                type="button"
                onClick={runDiag}
                className="text-[10px] text-slate-400 hover:text-slate-700 underline-offset-2 hover:underline"
                data-testid="fees-diag-btn"
                title="Copy PDF diagnostics JSON to clipboard — for support."
              >
                Diagnose PDF data
              </button>
            </div>
          )}
        </>
      )}

      <SdltDialog
        open={sdltOpen}
        onClose={() => setSdltOpen(false)}
        caseId={caseId}
        sdltParams={doc.sdlt_params}
        propertyRows={doc.property}
        onApplied={(total, params) => {
          setDoc((d) => {
            const next = { ...d, sdlt_params: params };
            next.property = next.property.map((r) => (
              (r.label || "").toLowerCase() === "sdlt"
                ? { ...r, amount: total, vat: 0, total }
                : r
            ));
            return next;
          });
          setDirty(true);
          setSdltOpen(false);
        }}
      />

      <Dialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <DialogContent data-testid="fees-delete-warning-dialog">
          <DialogHeader>
            <DialogTitle>Remove a billed disbursement?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            <strong>{pendingDelete?.label}</strong> is a recoverable disbursement that is also
            billed to your firm. Deleting it removes it from the completion statement <em>and</em>{" "}
            from your Usage &amp; Billing report immediately.
          </p>
          <p className="text-xs text-slate-400">
            It will be re-added automatically if the underlying work (e.g. a new ID check) happens again.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPendingDelete(null)}
              data-testid="fees-delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saving}
              className="text-white bg-rose-600 hover:bg-rose-700"
              onClick={async () => {
                const pd = pendingDelete;
                setPendingDelete(null);
                if (pd) await confirmBillableDelete(pd.cat, pd.rowId);
              }}
              data-testid="fees-delete-confirm-btn"
            >
              Delete &amp; save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeeSection({ category, rows, totals, isSolicitor, canOverrideLockedFees, onChange, onAdd, onRemove, onCalcSdlt }) {
  return (
    <div className="mb-3" data-testid={`fees-section-${category.key}`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{category.title}</p>
        <div className="flex items-center gap-2">
          {totals && (
            <span className="text-[10px] text-slate-400">
              {fmt(totals.total)}{category.vatable && totals.vat > 0 ? ` (inc. ${fmt(totals.vat)} VAT)` : ""}
            </span>
          )}
          {isSolicitor && category.allowAdd && (
            <button
              type="button"
              onClick={onAdd}
              data-testid={`fees-add-${category.key}`}
              title="Add row"
              className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100"
            >
              <Plus weight="bold" className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        {rows.length === 0 && (
          <p className="text-[11px] text-slate-400 italic">No rows.</p>
        )}
        {rows.map((r) => {
          const isSdltRow = category.key === "property" && (r.label || "").toLowerCase() === "sdlt";
          const lbl = (r.label || "").toLowerCase();
          const isCredit = category.key === "property" && (lbl.includes("deposit") || lbl.includes("allowance"));
          return (
            <FeeRow
              key={r.id}
              row={r}
              vatable={category.vatable}
              isIncoming={category.isIncoming}
              isCredit={isCredit}
              isSolicitor={isSolicitor}
              canOverrideLockedFees={canOverrideLockedFees}
              onChange={(patch) => onChange(r.id, patch)}
              onRemove={() => onRemove(r.id)}
              extraAction={isSdltRow ? (
                <button
                  type="button"
                  onClick={onCalcSdlt}
                  className="text-[10px] font-medium text-blue-600 hover:underline inline-flex items-center gap-0.5"
                  data-testid="fees-sdlt-calc-btn"
                  title="Calculate SDLT"
                >
                  <Calculator weight="bold" className="w-2.5 h-2.5" /> Calc
                </button>
              ) : null}
            />
          );
        })}
      </div>
    </div>
  );
}

function FeeRow({ row, vatable, isIncoming, isCredit, isSolicitor, canOverrideLockedFees, onChange, onRemove, extraAction }) {
  const isLockedLine = BILLABLE_AUTO_LABELS.includes((row.label || "").trim()) && !canOverrideLockedFees;
  return (
    <div className="grid grid-cols-[1fr_auto] gap-1 items-center" data-testid={`fees-row-${row.id}`}>
      <Input
        value={row.label}
        onChange={(e) => onChange({ label: e.target.value })}
        placeholder="Description"
        disabled={!isSolicitor}
        className="h-7 text-xs"
        data-testid={`fees-row-label-${row.id}`}
      />
      <div className="flex items-center gap-1">
        <Input
          value={row.amount}
          onChange={(e) => onChange({ amount: Number(e.target.value) || 0 })}
          type="number"
          step="0.01"
          disabled={!isSolicitor}
          className="h-7 text-xs w-24 text-right"
          data-testid={`fees-row-amount-${row.id}`}
        />
        {isSolicitor && (
          isLockedLine ? (
            <span
              data-testid={`fees-row-locked-${row.id}`}
              title="This fee is locked — only Steve or Jon can remove it. You can still edit the amount."
              className="w-6 h-6 flex items-center justify-center rounded text-slate-300 cursor-not-allowed"
            >
              <Lock weight="bold" className="w-3 h-3" />
            </span>
          ) : (
            <button
              type="button"
              onClick={onRemove}
              data-testid={`fees-row-delete-${row.id}`}
              title="Remove row"
              className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
            >
              <Trash weight="bold" className="w-3 h-3" />
            </button>
          )
        )}
      </div>
      <div className="col-span-2 flex items-center justify-between gap-3 pl-1 -mt-0.5">
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          {vatable && (
            <span data-testid={`fees-row-vat-${row.id}`}>VAT {fmt(row.vat)} · Total {fmt(row.total)}</span>
          )}
          {!vatable && row.total != null && (
            <span>{isCredit ? `− ${fmt(Math.abs(row.total || row.amount))}` : fmt(row.total || row.amount)}</span>
          )}
          {extraAction}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-600">
          <label className="flex items-center gap-1 cursor-pointer">
            <Checkbox
              checked={row.add_to_completion}
              onCheckedChange={(v) => onChange({ add_to_completion: !!v })}
              disabled={!isSolicitor}
              data-testid={`fees-row-completion-${row.id}`}
            />
            <span>Comp</span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <Checkbox
              checked={row.add_to_invoice}
              onCheckedChange={(v) => onChange({ add_to_invoice: !!v })}
              disabled={!isSolicitor}
              data-testid={`fees-row-invoice-${row.id}`}
            />
            <span>Inv</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function Total({ label, value, emphasise }) {
  return (
    <div className={`flex justify-between ${emphasise ? "font-semibold text-slate-900 pt-1" : "text-slate-600"}`}>
      <span>{label}</span>
      <span>{fmt(value || 0)}</span>
    </div>
  );
}

function SdltDialog({ open, onClose, caseId, sdltParams, propertyRows, onApplied }) {
  const [params, setParams] = useState({
    property_value: 0,
    first_time_buyer: false,
    second_home: false,
    ltd_company: false,
    commercial: false,
    non_uk_resident: false,
    lease_npv: 0,
    linked_consideration: 0,
    corporate_body: false,
  });
  // Helper inputs for converting annual rent → NPV. HMRC NPV formula:
  //    NPV = sum over years 1..n of rent_y / (1 + 0.035)^y
  // For a constant rent R over n years:
  //    NPV = R × (1 − (1.035)^−n) / 0.035
  const [rentAnnual, setRentAnnual] = useState(0);
  const [rentYears, setRentYears] = useState(5);
  const [calc, setCalc] = useState(null);
  const [calcing, setCalcing] = useState(false);

  useEffect(() => {
    if (!open) return;
    let seedPrice = sdltParams?.property_value || 0;
    if (!seedPrice && propertyRows) {
      const pr = propertyRows.find((r) => (r.label || "").toLowerCase().includes("price"));
      if (pr) seedPrice = pr.amount;
    }
    setParams({
      property_value: seedPrice || 0,
      first_time_buyer: !!sdltParams?.first_time_buyer,
      second_home: !!sdltParams?.second_home,
      ltd_company: !!sdltParams?.ltd_company,
      commercial: !!sdltParams?.commercial,
      non_uk_resident: !!sdltParams?.non_uk_resident,
      lease_npv: sdltParams?.lease_npv || 0,
      linked_consideration: sdltParams?.linked_consideration || 0,
      corporate_body: !!sdltParams?.corporate_body,
    });
    setRentAnnual(0);
    setRentYears(5);
    setCalc(null);
  }, [open, sdltParams, propertyRows]);

  // Recompute NPV whenever the helper inputs change so the user sees it
  // live before clicking Calculate.
  const computedNpv = useMemo(() => {
    const r = Number(rentAnnual) || 0;
    const n = Math.max(1, Number(rentYears) || 0);
    if (r <= 0) return 0;
    const factor = (1 - Math.pow(1.035, -n)) / 0.035;
    return Math.round(r * factor * 100) / 100;
  }, [rentAnnual, rentYears]);

  const useComputedNpv = () => setParams((p) => ({ ...p, lease_npv: computedNpv }));

  const runCalc = useCallback(async () => {
    setCalcing(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/fees/sdlt/calculate`, params);
      setCalc(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Calculation failed");
    } finally {
      setCalcing(false);
    }
  }, [caseId, params]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="sdlt-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Stamp Duty Land Tax</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <Label className="text-xs">Property value / lease premium (£)</Label>
            <Input
              type="number"
              value={params.property_value}
              onChange={(e) => setParams((p) => ({ ...p, property_value: Number(e.target.value) || 0 }))}
              data-testid="sdlt-value-input"
            />
          </div>
          {[
            ["first_time_buyer", "First-time buyer"],
            ["second_home", "Additional property / second home"],
            ["ltd_company", "Limited company purchase"],
            ["non_uk_resident", "Non-UK resident (+2% surcharge)"],
            ["corporate_body", "Corporate body — flat 17% on residential >£500k (Sched 4A)"],
            ["commercial", "Commercial / mixed-use (uses non-residential SDLT rates)"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={params[key]}
                onCheckedChange={(v) => setParams((p) => {
                  if (key === "commercial" && v) {
                    return { ...p, commercial: true, first_time_buyer: false, second_home: false, ltd_company: false, non_uk_resident: false, corporate_body: false };
                  }
                  // Corporate body flat 17% is mutually exclusive with FTB
                  // (HMRC SDLTM09810 — no relief on non-natural persons)
                  // and with the +2% non-UK surcharge (the flat 17% replaces
                  // banded rates entirely so surcharges don't apply).
                  if (key === "corporate_body" && v) {
                    return { ...p, corporate_body: true, first_time_buyer: false, commercial: false, non_uk_resident: false };
                  }
                  return { ...p, [key]: !!v };
                })}
                data-testid={`sdlt-${key.replace(/_/g, "-")}`}
                disabled={
                  (key !== "commercial" && params.commercial) ||
                  (key === "first_time_buyer" && params.corporate_body) ||
                  (key === "non_uk_resident" && params.corporate_body)
                }
              />
              <span className={`text-xs ${
                ((key !== "commercial" && params.commercial) ||
                  (key === "first_time_buyer" && params.corporate_body) ||
                  (key === "non_uk_resident" && params.corporate_body))
                  ? "text-slate-400" : ""
              }`}>{label}</span>
            </label>
          ))}

          {/* New lease — premium SDLT plus NPV-on-rent SDLT */}
          <div className="border border-slate-200 bg-amber-50/40 rounded-lg p-2.5 space-y-2">
            <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">New Lease — Tax on Rent (NPV)</p>
            <p className="text-[11px] text-slate-500 leading-snug">
              SDLT on a new lease applies to the premium <em>and</em> separately to the Net Present Value of rent payable. Helper computes NPV at HMRC's 3.5% discount rate.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-500">Annual rent (£)</Label>
                <Input type="number" value={rentAnnual} onChange={(e) => setRentAnnual(Number(e.target.value) || 0)} className="h-8 text-xs" data-testid="sdlt-rent-annual" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-500">Years</Label>
                <Input type="number" value={rentYears} onChange={(e) => setRentYears(Number(e.target.value) || 0)} className="h-8 text-xs" data-testid="sdlt-rent-years" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-600">
                Computed NPV: <span className="font-semibold tabular-nums">{fmt(computedNpv)}</span>
              </div>
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={useComputedNpv} data-testid="sdlt-npv-use-btn" disabled={!computedNpv}>
                Use as NPV
              </Button>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-500">Lease NPV (£) — applied to SDLT</Label>
              <Input
                type="number"
                value={params.lease_npv}
                onChange={(e) => setParams((p) => ({ ...p, lease_npv: Number(e.target.value) || 0 }))}
                className="h-8 text-xs"
                data-testid="sdlt-lease-npv"
              />
            </div>
          </div>

          {/* Linked transactions — HMRC FA 2003 s.108 */}
          <div className="border border-slate-200 bg-violet-50/40 rounded-lg p-2.5 space-y-1.5">
            <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Linked Transactions</p>
            <p className="text-[11px] text-slate-500 leading-snug">
              If this purchase is part of a single arrangement with other property purchases between the same parties, enter the combined consideration of the OTHER linked properties. SDLT will be banded on the total and prorated.
            </p>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-500">Other linked consideration (£)</Label>
              <Input
                type="number"
                value={params.linked_consideration}
                onChange={(e) => setParams((p) => ({ ...p, linked_consideration: Number(e.target.value) || 0 }))}
                className="h-8 text-xs"
                data-testid="sdlt-linked-consideration"
              />
            </div>
          </div>

          <Button onClick={runCalc} disabled={calcing} variant="outline" size="sm" className="w-full h-8 text-xs gap-1.5" data-testid="sdlt-calc-run-btn">
            <Calculator weight="bold" className="w-3.5 h-3.5" /> {calcing ? "Calculating…" : "Calculate"}
          </Button>
          {calc && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1 text-xs" data-testid="sdlt-result">
              <div className="flex justify-between font-semibold text-slate-900">
                <span className="flex items-center gap-1 flex-wrap">
                  Total SDLT
                  {calc.schedule === "non_residential" && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700">Non-residential</span>}
                  {calc.non_uk_resident && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700">+2% Non-UK</span>}
                  {calc.linked_active && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">Linked</span>}
                  {calc.npv_total > 0 && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Lease NPV</span>}
                  {calc.corporate_flat_active && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800">Corporate 17%</span>}
                  {calc.corporate_body && !calc.corporate_flat_active && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600" title="Corporate flat 17% only kicks in above £500,000">Corporate ≤£500k</span>}
                </span>
                <span>{fmt(calc.total)}</span>
              </div>
              {(calc.npv_total > 0 || calc.linked_active) && (
                <div className="border-t border-slate-200 mt-1 pt-1 space-y-0.5 text-[11px]">
                  <div className="flex justify-between text-slate-600">
                    <span>Premium SDLT{calc.linked_active ? " (prorated)" : ""}</span>
                    <span className="tabular-nums">{fmt(calc.premium_total)}</span>
                  </div>
                  {calc.npv_total > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Rent NPV SDLT</span>
                      <span className="tabular-nums">{fmt(calc.npv_total)}</span>
                    </div>
                  )}
                  {calc.linked_active && (
                    <p className="text-[10px] text-violet-700 italic pt-0.5">
                      Computed on combined £{calc.linked_total.toLocaleString("en-GB")} (this property = {Math.round((params.property_value / calc.linked_total) * 100)}%)
                    </p>
                  )}
                </div>
              )}
              <div className="border-t border-slate-200 mt-1 pt-1 space-y-0.5 text-[11px] text-slate-500">
                {(calc.bands || []).map((b, i) => (
                  <div key={`pre-${i}`} className="flex justify-between">
                    <span>Premium band {i + 1} @ {b.rate}%</span>
                    <span>{fmt(b.tax || 0)}</span>
                  </div>
                ))}
                {(calc.npv_bands || []).map((b, i) => (
                  <div key={`npv-${i}`} className="flex justify-between">
                    <span>NPV band {i + 1} @ {b.rate}%</span>
                    <span>{fmt(b.tax || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!calc}
            onClick={() => onApplied(calc.total || 0, params)}
            className="text-white"
            style={{ background: "#100f5d" }}
            data-testid="sdlt-apply-btn"
          >
            Apply £{(calc?.total || 0).toLocaleString("en-GB")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
